import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';
import moment, { Moment } from 'moment';
import React, { forwardRef, ReactNode, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { OnChangeValue } from 'react-select/dist/declarations/src/types';
import { CHURCH_FOUNDING_DATE } from './constants';
import { useGetLeadersQuery } from './hooks/useGetLeadersQuery';
import { checkWasApostle } from './hooks/usePresentPositionCallDate';
import useRouteChanges from './hooks/useRouteChanges';
import { Gender, GetLeadersResponse, Leader } from './types';

type LeaderOption = { label: string, value: string } & Leader;

interface StepButtonProps {
  onClick: () => void,
  disabled: boolean,
  label: string,
  children: ReactNode,
}

const StepButton = ({ onClick, disabled, label, children }: StepButtonProps) => (
  <button
    className={[
      'rounded-full h-9 w-9 md:h-10 md:w-10 flex items-center justify-center shadow',
      'bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200',
      disabled ? 'opacity-50 cursor-not-allowed' : '',
    ].join(' ')}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
  >
    {children}
  </button>
);

const DateInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => (
  <input
    {...props}
    ref={ref}
    className={[
      'w-60 md:w-[22rem] px-2 bg-transparent text-center',
      'text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white',
      'placeholder-gray-500 rounded-md',
      'focus:outline-none focus:ring-2 focus:ring-blue-400',
    ].join(' ')}
  />
));

interface Props {
  loading: boolean,
  prevLeaderChangeDate: GetLeadersResponse['meta']['pagination']['prevLeaderChangeDate'],
  nextLeaderChangeDate: GetLeadersResponse['meta']['pagination']['nextLeaderChangeDate'],
  prevProphetChangeDate: GetLeadersResponse['meta']['pagination']['prevProphetChangeDate'],
  nextProphetChangeDate: GetLeadersResponse['meta']['pagination']['nextProphetChangeDate'],
}

const DateHeadline = ({
  loading,
  prevLeaderChangeDate,
  nextLeaderChangeDate,
  prevProphetChangeDate,
  nextProphetChangeDate,
}: Props) => {
  const { date, setDate, gender, partialUpdateSearchParams } = useRouteChanges();

  const [showLeaderSelect, setShowLeaderSelect] = useState(false);

  const { data } = useGetLeadersQuery();

  const leaderOptions: LeaderOption[] = useMemo(() => {
    return (data?.data || []).map(leader => ({
      label: leader.name,
      value: leader.callDate,
      ...leader,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const onLeaderChange = (leaderOption: OnChangeValue<LeaderOption, false>) => {
    if (leaderOption?.value) {
      partialUpdateSearchParams({
        date: moment(leaderOption.value).format('YYYY-MM-DD'),
        calledBy: '',
        // non apostles only appear with the wider position list and no gender filter
        ...(checkWasApostle(leaderOption) ? {} : { gender: '', showAll: 'true' }),
      });
    } else {
      setDate(null);
    }
    setShowLeaderSelect(false);
  };

  const isToday = !!date && moment().isSame(date, 'day');
  const prophetDisabled = gender === Gender.FEMALE;

  // typing a year digit by digit parses as year 1, 19, 193... so out of
  // range dates are ignored until the typed date lands inside the era
  const handleDateChange = (value: Date | null) => {
    if (value && (moment(value).isBefore(CHURCH_FOUNDING_DATE) || moment(value).isAfter(moment(), 'day'))) {
      return;
    }
    setDate(value);
  };

  return (
    <div>
      <div className="flex items-center justify-center gap-2 md:gap-3">
        <StepButton
          onClick={() => setDate(moment(prevProphetChangeDate).toDate())}
          disabled={!prevProphetChangeDate || prophetDisabled || loading}
          label="Last prophet change"
        >
          <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
        </StepButton>
        <StepButton
          onClick={() => setDate(moment(prevLeaderChangeDate).toDate())}
          disabled={!prevLeaderChangeDate || loading}
          label="Last leader change"
        >
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </StepButton>
        <div className="[&_.react-datepicker-wrapper]:w-auto">
          <DatePicker
            selected={date?.toDate()}
            onChange={handleDateChange}
            customInput={<DateInput />}
            dateFormat={['MMMM d, yyyy', 'M/d/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd']}
            placeholderText="All time"
            minDate={moment(CHURCH_FOUNDING_DATE).toDate()}
            maxDate={moment().toDate()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
        </div>
        <StepButton
          onClick={() => setDate(moment(nextLeaderChangeDate).toDate())}
          disabled={!nextLeaderChangeDate || loading}
          label="Next leader change"
        >
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </StepButton>
        <StepButton
          onClick={() => setDate(moment(nextProphetChangeDate).toDate())}
          disabled={!nextProphetChangeDate || prophetDisabled || loading}
          label="Next prophet change"
        >
          <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
        </StepButton>
      </div>
      <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
        type a date or click to pick
      </div>
      <div className="flex items-center justify-center gap-2 mt-2 text-sm">
        <button
          className={[
            'text-blue-600 dark:text-blue-400 hover:underline font-bold',
            isToday ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
          onClick={() => setDate(moment().toDate())}
          disabled={isToday}
        >
          Today
        </button>
        <span className="text-gray-400 dark:text-gray-500">·</span>
        <button
          className="text-blue-600 dark:text-blue-400 hover:underline font-bold"
          onClick={() => setShowLeaderSelect(!showLeaderSelect)}
        >
          Select leader…
        </button>
        {date && <>
          <span className="text-gray-400 dark:text-gray-500">·</span>
          <button
            className="text-blue-600 dark:text-blue-400 hover:underline font-bold"
            onClick={() => setDate(null)}
          >
            Clear
          </button>
        </>}
      </div>
      {showLeaderSelect && <div className="relative z-20">
        <div className="absolute left-1/2 -translate-x-1/2 top-2 w-72 text-left text-base font-normal">
        <Select<LeaderOption>
          name="leader"
          autoFocus
          classNames={{
            container: () => 'shadow',
            placeholder: () => 'text-sm',
            control: () => 'dark:!bg-gray-800 dark:!border-gray-600',
            singleValue: () => 'dark:!text-gray-200',
            input: () => 'dark:!text-gray-200',
            menu: () => 'dark:!bg-gray-800',
            option: (state) => `dark:!text-gray-200 ${state.isFocused ? 'dark:!bg-gray-700' : 'dark:!bg-gray-800'}`,
          }}
          onChange={onLeaderChange}
          options={leaderOptions}
          value={null}
          menuPortalTarget={document.body} // keeps dropdown on top of everything
        />
        </div>
      </div>}
    </div>
  );
};

export default DateHeadline;
